import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Address } from 'viem';
import { normalizeAddress } from './address';
import { GhostContext, type GhostContextValue } from './context';
import { t } from '../i18n/i18n';

export const GHOST_PARAM = 'ghost';

function ghostParam(search: string): string | null {
  return new URLSearchParams(search).get(GHOST_PARAM);
}

function withGhost(search: string, address: Address | null): string {
  const p = new URLSearchParams(search);
  if (address) p.set(GHOST_PARAM, address.toLowerCase());
  else p.delete(GHOST_PARAM);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * Ghost (watch-address) mode, trade.xyz style. The address lives in `?ghost=0x...`; once set it is
 * re-appended after in-app navigation (NavLinks drop the query) until the user exits ghost mode.
 * Must be rendered inside a Router.
 */
export function GhostProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [ghostAddress, setGhostAddress] = useState<Address | null>(() => normalizeAddress(ghostParam(location.search)));
  const [ghostError, setGhostError] = useState<string | null>(null);

  const replaceSearch = useCallback(
    (search: string) =>
      navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true, state: location.state }),
    [navigate, location.pathname, location.hash, location.state],
  );

  // URL → state, and keep the param in the URL while ghosting. Only a *changed* URL may override state:
  // router navigations can commit after our own setState (v7_startTransition), and a stale ?ghost= must not
  // undo enterGhost/exitGhost.
  const lastSearchRef = useRef<string | null>(null);
  useEffect(() => {
    const searchChanged = lastSearchRef.current !== location.search;
    lastSearchRef.current = location.search;
    const raw = ghostParam(location.search);
    if (raw !== null) {
      if (!searchChanged) return;
      const a = normalizeAddress(raw);
      if (a) {
        if (a !== ghostAddress) setGhostAddress(a);
      } else {
        setGhostAddress(null);
        setGhostError(t('wallet.ghost.not_valid', { input: raw.slice(0, 64) }));
        replaceSearch(withGhost(location.search, null));
      }
    } else if (ghostAddress) {
      replaceSearch(withGhost(location.search, ghostAddress));
    }
  }, [location.search, ghostAddress, replaceSearch]);

  const enterGhost = useCallback(
    (input: string) => {
      const a = normalizeAddress(input);
      if (!a) return false;
      setGhostError(null);
      setGhostAddress(a);
      replaceSearch(withGhost(location.search, a));
      return true;
    },
    [location.search, replaceSearch],
  );

  const exitGhost = useCallback(() => {
    setGhostAddress(null);
    replaceSearch(withGhost(location.search, null));
  }, [location.search, replaceSearch]);

  const clearGhostError = useCallback(() => setGhostError(null), []);

  const value = useMemo<GhostContextValue>(
    () => ({ ghostAddress, ghostError, enterGhost, exitGhost, clearGhostError }),
    [ghostAddress, ghostError, enterGhost, exitGhost, clearGhostError],
  );
  return <GhostContext.Provider value={value}>{children}</GhostContext.Provider>;
}
